import {useMemo, useReducer} from 'react';
import {AnyAction} from '@reduxjs/toolkit';

import {Tail} from '~shared/types';

// any is required here to have correct type inference
/* eslint-disable @typescript-eslint/no-explicit-any */
type Method<State> = (state: State, payload?: any) => State;

type UseMethodsActionCreator<ActionMethod extends Method<any>> = (
    ...args: Tail<Parameters<ActionMethod>>
) => void;

type UseMethodsActionCreators<Methods extends Record<string, Method<any>>> = {
    [Key in keyof Methods]: UseMethodsActionCreator<Methods[Key]>;
};
/* eslint-enable @typescript-eslint/no-explicit-any */

export function useMethods<State, Methods extends Record<string, Method<State>>>(
    methods: Methods | (() => Methods),
    initialState: State,
): [State, UseMethodsActionCreators<Methods>] {
    const reducer = (state: State, action: AnyAction) => {
        const {type, payload} = action;
        const actualMethods = typeof methods === 'function' ? methods() : methods;

        const method = actualMethods[type];

        if (!method) {
            return state;
        }

        return method(state, payload);
    };

    const [state, dispatch] = useReducer(reducer, initialState);

    const methodsActions = useMemo(() => {
        const actualMethods = typeof methods === 'function' ? methods() : methods;

        return Object.keys(actualMethods).reduce(
            (result: Record<string, (payload?: unknown) => void>, type) => {
                result[type] = (payload?: unknown) => {
                    dispatch({type, payload});
                };
                return result;
            },
            {},
        ) as UseMethodsActionCreators<Methods>;
        // we only use keys of the `methods` object, therefore
        // we could safely omit it from the deps
        // eslint-ignore-next-line react-hooks/exhaustive-deps
    }, []);

    return [state, methodsActions];
}
